import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, readApiError } from '../../lib/api';
import { formatCop } from '../../lib/format';
import { hasPermission, useMe } from '../../hooks/useAuth';
import { creditNotePdfUrl, issueCreditNote } from '../credit-notes/credit-notes.api';

type InvoiceStatus = 'DRAFT' | 'ISSUED' | 'DIAN_PENDING' | 'DIAN_APPROVED' | 'DIAN_REJECTED' | 'CANCELLED';

interface InvoiceRow {
  id: number;
  number: string;
  status: InvoiceStatus;
  issuedAt: string;
  total: string;
  cufe: string | null;
  customer: { firstName: string; lastName: string; idDocument: string };
  cashier: { fullName: string };
  serviceOrder: { folio: string } | null;
}

const STATUS_COLORS: Record<InvoiceStatus, string> = {
  DRAFT: '#6b7280',
  ISSUED: '#0ea5e9',
  DIAN_PENDING: '#f59e0b',
  DIAN_APPROVED: '#10b981',
  DIAN_REJECTED: '#dc2626',
  CANCELLED: '#9ca3af',
};

const STATUS_LABELS: Record<InvoiceStatus, string> = {
  DRAFT: 'Borrador',
  ISSUED: 'Emitida',
  DIAN_PENDING: 'DIAN pendiente',
  DIAN_APPROVED: 'DIAN aprobada',
  DIAN_REJECTED: 'DIAN rechazada',
  CANCELLED: 'Anulada',
};

export function InvoicesPage() {
  const me = useMe();
  const canWrite = hasPermission(me.data, 'accounting.write');
  const qc = useQueryClient();

  const [q, setQ] = useState('');
  const [status, setStatus] = useState<InvoiceStatus | ''>('');
  const [voiding, setVoiding] = useState<InvoiceRow | null>(null);
  const [creditingNote, setCreditingNote] = useState<InvoiceRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['invoices', q, status],
    queryFn: async () => {
      const { data } = await api.get<{ data: InvoiceRow[] }>('/api/invoices', {
        params: { q: q || undefined, status: status || undefined, limit: 100 },
      });
      return data.data;
    },
  });

  const dianMut = useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post<{ status: string; cufe: string }>(`/api/dian/invoices/${id}/issue`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invoices'] }),
    onError: (err) => setError(readApiError(err).message),
  });

  const voidMut = useMutation({
    mutationFn: async (vars: { id: number; reason: string }) => {
      await api.post(`/api/invoices/${vars.id}/void`, { reason: vars.reason });
    },
    onSuccess: () => {
      setVoiding(null);
      void qc.invalidateQueries({ queryKey: ['invoices'] });
      void qc.invalidateQueries({ queryKey: ['kpis'] });
    },
    onError: (err) => setError(readApiError(err).message),
  });

  const creditNoteMut = useMutation({
    mutationFn: async (vars: { id: number; reason: string }) => {
      return await issueCreditNote(vars.id, vars.reason);
    },
    onSuccess: (r) => {
      setCreditingNote(null);
      setSuccess(`Nota crédito ${r.number} emitida (${r.status}). Stock restaurado en ${r.stockRestored} ítems.`);
      setTimeout(() => setSuccess(null), 6000);
      void qc.invalidateQueries({ queryKey: ['invoices'] });
      void qc.invalidateQueries({ queryKey: ['credit-notes'] });
      // Auto-open the credit-note PDF in a new tab
      window.open(creditNotePdfUrl(r.creditNoteId), '_blank', 'noopener,noreferrer');
    },
    onError: (err) => setError(readApiError(err).message),
  });

  return (
    <section>
      <h1 style={{ marginTop: 0 }}>Facturas</h1>

      {error && <p className="error">{error}</p>}

      <div className="card" style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        <input
          className="input"
          placeholder="Buscar por número, cliente, cédula..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ flex: 1 }}
        />
        <select
          className="input"
          value={status}
          onChange={(e) => setStatus(e.target.value as InvoiceStatus | '')}
          style={{ width: 200 }}
        >
          <option value="">Todos los estados</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
          <thead>
            <tr style={{ background: 'var(--color-bg)', textAlign: 'left' }}>
              <th style={{ padding: '0.5rem 1rem' }}>Número</th>
              <th style={{ padding: '0.5rem 1rem' }}>Fecha</th>
              <th style={{ padding: '0.5rem 1rem' }}>Cliente</th>
              <th style={{ padding: '0.5rem 1rem' }}>Cajero</th>
              <th style={{ padding: '0.5rem 1rem' }}>OS</th>
              <th style={{ padding: '0.5rem 1rem' }}>Total</th>
              <th style={{ padding: '0.5rem 1rem' }}>Estado</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.data?.map((inv) => (
              <tr key={inv.id} style={{ borderTop: '1px solid var(--color-border)' }}>
                <td style={{ padding: '0.5rem 1rem' }}>
                  <code>{inv.number}</code>
                  {inv.cufe && (
                    <div style={{ fontSize: '0.7rem', color: 'var(--color-muted)' }} title={inv.cufe}>
                      CUFE {inv.cufe.slice(0, 12)}…
                    </div>
                  )}
                </td>
                <td style={{ padding: '0.5rem 1rem' }}>
                  {new Date(inv.issuedAt).toLocaleDateString('es-CO')}
                </td>
                <td style={{ padding: '0.5rem 1rem' }}>
                  {inv.customer.firstName} {inv.customer.lastName}
                  <br />
                  <small style={{ color: 'var(--color-muted)' }}>{inv.customer.idDocument}</small>
                </td>
                <td style={{ padding: '0.5rem 1rem' }}>{inv.cashier.fullName}</td>
                <td style={{ padding: '0.5rem 1rem' }}>
                  {inv.serviceOrder ? <code>{inv.serviceOrder.folio}</code> : '—'}
                </td>
                <td style={{ padding: '0.5rem 1rem' }}>{formatCop(inv.total)}</td>
                <td style={{ padding: '0.5rem 1rem' }}>
                  <span
                    style={{
                      background: STATUS_COLORS[inv.status],
                      color: '#fff',
                      padding: '2px 10px',
                      borderRadius: 999,
                      fontSize: '0.75rem',
                    }}
                  >
                    {STATUS_LABELS[inv.status]}
                  </span>
                </td>
                <td style={{ padding: '0.5rem 1rem', textAlign: 'right' }}>
                  {canWrite && inv.status === 'ISSUED' && (
                    <button
                      className="btn"
                      style={{ padding: '4px 10px', fontSize: '0.8rem', marginRight: 6 }}
                      disabled={dianMut.isPending}
                      onClick={() => dianMut.mutate(inv.id)}
                    >
                      Emitir DIAN
                    </button>
                  )}
                  {canWrite && (inv.status === 'ISSUED' || inv.status === 'DIAN_PENDING' || inv.status === 'DIAN_REJECTED') && (
                    <button
                      className="btn"
                      style={{ padding: '4px 10px', fontSize: '0.8rem', color: 'var(--color-danger)' }}
                      onClick={() => setVoiding(inv)}
                    >
                      Anular
                    </button>
                  )}
                  {canWrite && inv.status === 'DIAN_APPROVED' && (
                    <button
                      className="btn"
                      style={{ padding: '4px 10px', fontSize: '0.8rem', color: 'var(--color-accent)' }}
                      onClick={() => setCreditingNote(inv)}
                      title="Emitir Nota Crédito DIAN"
                    >
                      Nota Crédito
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {list.data?.length === 0 && (
              <tr>
                <td colSpan={8} style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-muted)' }}>
                  Sin facturas.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {voiding && (
        <VoidModal
          invoice={voiding}
          onCancel={() => setVoiding(null)}
          onSubmit={(reason) => voidMut.mutate({ id: voiding.id, reason })}
          submitting={voidMut.isPending}
        />
      )}
      {creditingNote && (
        <CreditNoteModal
          invoice={creditingNote}
          onCancel={() => setCreditingNote(null)}
          onSubmit={(reason) => creditNoteMut.mutate({ id: creditingNote.id, reason })}
          submitting={creditNoteMut.isPending}
        />
      )}
      {success && (
        <div
          style={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            background: 'var(--color-accent)',
            color: '#1a1a1a',
            padding: '0.75rem 1rem',
            borderRadius: 'var(--radius)',
            zIndex: 200,
          }}
        >
          {success}
        </div>
      )}
    </section>
  );
}

function VoidModal(props: {
  invoice: InvoiceRow;
  onCancel: () => void;
  onSubmit: (reason: string) => void;
  submitting: boolean;
}) {
  const [reason, setReason] = useState('');
  return (
    <div style={modalBackdrop}>
      <form
        className="card"
        style={{ width: 460, borderColor: 'var(--color-danger)' }}
        onSubmit={(e) => {
          e.preventDefault();
          props.onSubmit(reason.trim());
        }}
      >
        <h3 style={{ marginTop: 0, color: 'var(--color-danger)' }}>Anular factura</h3>
        <p>
          Vas a anular la factura <strong>{props.invoice.number}</strong> por{' '}
          <strong>{formatCop(props.invoice.total)}</strong>.
        </p>
        <ul style={{ fontSize: '0.85rem', color: 'var(--color-muted)' }}>
          <li>Restaura el stock de los productos vendidos</li>
          <li>Genera un asiento contable reverso (D Ventas / D IVA / C Caja)</li>
          <li>NO se puede deshacer; la factura queda CANCELLED para siempre</li>
          <li>Si está aprobada por DIAN debes emitir nota crédito en su lugar</li>
        </ul>
        <div className="field">
          <label>Razón (mínimo 3 caracteres)</label>
          <textarea
            className="input"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            minLength={3}
            autoFocus
          />
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            type="submit"
            className="btn"
            style={{ background: 'var(--color-danger)', color: '#fff', borderColor: 'var(--color-danger)' }}
            disabled={props.submitting || reason.trim().length < 3}
          >
            {props.submitting ? 'Anulando...' : 'Confirmar anulación'}
          </button>
          <button type="button" className="btn" onClick={props.onCancel}>Cancelar</button>
        </div>
      </form>
    </div>
  );
}

function CreditNoteModal(props: {
  invoice: InvoiceRow;
  onCancel: () => void;
  onSubmit: (reason: string) => void;
  submitting: boolean;
}) {
  const [reason, setReason] = useState('');
  return (
    <div style={modalBackdrop}>
      <form
        className="card"
        style={{ width: 480, borderColor: 'var(--color-accent)' }}
        onSubmit={(e) => {
          e.preventDefault();
          props.onSubmit(reason.trim());
        }}
      >
        <h3 style={{ marginTop: 0, color: 'var(--color-accent)' }}>Emitir Nota Crédito DIAN</h3>
        <p>
          La factura <strong>{props.invoice.number}</strong> ya tiene CUFE aprobado por la DIAN.
          Una nota crédito legalmente reversa la operación.
        </p>
        <ul style={{ fontSize: '0.85rem', color: 'var(--color-muted)' }}>
          <li>Restaura stock de los productos</li>
          <li>Genera asiento contable reverso (D Ventas + D IVA / C Caja)</li>
          <li>Emite el documento ante la DIAN para obtener un nuevo CUFE</li>
          <li>La factura original conserva su estado DIAN_APPROVED (la ley lo exige)</li>
        </ul>
        <div className="field">
          <label>Razón de la nota crédito (mínimo 3 caracteres)</label>
          <textarea
            className="input"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            minLength={3}
            autoFocus
            placeholder="Ej. Devolución de mercancía, descuento aprobado..."
          />
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={props.submitting || reason.trim().length < 3}
          >
            {props.submitting ? 'Emitiendo...' : 'Emitir Nota Crédito'}
          </button>
          <button type="button" className="btn" onClick={props.onCancel}>
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
}

const modalBackdrop: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.4)',
  display: 'grid',
  placeItems: 'center',
  zIndex: 100,
};
